import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Error from "./Error";
import Loading from "./Loading";
import { Document, Page, View, Text, Image, StyleSheet, PDFViewer } from "@react-pdf/renderer";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChartCapture from "../components/ChartCapture";

const logo = "https://wildlife-investments-prod-images.s3.us-east-2.amazonaws.com/wildlifeinvestments-horizontalgrey-nobackground.png";
const logoNoText = "https://wildlife-investments-prod-images.s3.us-east-2.amazonaws.com/wildlifeinvestments-verticalgrey-cropped-nobackground.png";
const whiteLogoNoText = "https://wildlife-investments-prod-images.s3.us-east-2.amazonaws.com/wildlifeinvestments-verticalwhite-cropped-nobackground.png";

function BuckReportPDF({ currentProfile, isMobile }) {
    const [selectedSite, setSelectedSite] = useState(null);
    const [bucksObject, setBucksObject] = useState(null);
    const [bucksError, setBucksError] = useState('');
    const [orderedBucks, setOrderedBucks] = useState(null);
    const [buckStructure, setBuckStructure] = useState(null);
    const [buckAgeStructure, setBuckAgeStructure] = useState(null);
    const [buckDataFetched, setBuckDataFetched] = useState(false);
    const [imageStatsFetched, setImageStatsFetched] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const [buckAgeStructureGraphURL, setBuckAgeStructureGraphURL] = useState('');
    const [populationNumbersGraphURL, setPopulationNumbersGraphURL] = useState('');
    const [buckAgeStructureChartData, setBuckAgeStructureChartData] = useState(null);
    const [buckAgeStructureChartOptions, setBuckAgeStructureChartOptions] = useState(null);
    const [populationNumbersChartData, setPopulationNumbersChartData] = useState(null);
    const [populationNumbersChartOptions, setPopulationNumbersChartOptions] = useState(null);

    const buckAgeStructureGraphType = 'bar';
    const populationNumbersGraphType = 'pie';

    const { site } = useParams();

    let formattedSite = "site";
    if(site) {
        formattedSite = site.replaceAll("-wildlifeinvestments", "");
        let sites = formattedSite.split("-");
        formattedSite = sites.map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join(' ');
    }

    useEffect(() => {
        const preventDefault = (e) => e.preventDefault();
        if(!populationNumbersGraphURL && !buckAgeStructureGraphURL) {
            window.addEventListener('wheel', preventDefault, { passive: false });
            window.addEventListener('touchmove', preventDefault, { passive: false });
        } else {
            window.removeEventListener('wheel', preventDefault);
            window.removeEventListener('touchmove', preventDefault);
        }
    }, [populationNumbersGraphURL, buckAgeStructureGraphURL])

    useEffect(() => {
        if(currentProfile && currentProfile.sites) {
            currentProfile.sites.forEach((s) => {
                if(s.slug === site) {
                    setSelectedSite(s);
                }
            });
        }
    }, [currentProfile, site]);

    useEffect(() => {
        if(selectedSite && currentProfile) {
            selectedSite.getBuckInventory(currentProfile.accessToken).then((response) => {
                // console.log(response);
                if(response) {
                    setBucksObject(response);
                    setBuckDataFetched(true);
                } else {
                    setBucksError('Failed to get latest buck inventory, please refresh the page and try again.')
                }
            });
            selectedSite.getSiteStats(currentProfile.accessToken).then(() => {
                setImageStatsFetched(true);
            });
        }
    }, [selectedSite, currentProfile]);

    const sortByClassificationAndPoints = (a, b) => {
        // Define the priority for each classification
        const classificationOrder = {
          'Trophy': 1,
          'Management': 2,
          'Protect': 3
        };
      
        // First, compare classifications
        const classificationDiff = classificationOrder[a.classification] - classificationOrder[b.classification];
        if (classificationDiff !== 0) return classificationDiff;
      
        // If classifications are the same, compare points (in descending order)
        return b.points - a.points;
    };

    useEffect(() => {
        if(bucksObject) {
            const bucks = Object.values(bucksObject);
            const sortedBucks = bucks.sort(sortByClassificationAndPoints);
            setOrderedBucks(sortedBucks);
            let trophyBucks = [];
            let managementBucks = [];
            let protectBucks = [];
            let buckAgeStructureObject = {
                "1.5": 0,
                "2.5": 0,
                "3.5": 0,
                "4.5": 0,
                "5.5+": 0
            };
            sortedBucks.map((buck) => {
                if(buck.classification === "Trophy") {
                    trophyBucks.push(buck);
                } else if(buck.classification === "Management") {
                    managementBucks.push(buck);
                } else if(buck.classification === "Protect") {
                    protectBucks.push(buck);
                }

                if(buck.age < 2) {
                    Object.assign(buckAgeStructureObject, { "1.5": buckAgeStructureObject["1.5"] + 1 });
                } else if(buck.age < 3) {
                    Object.assign(buckAgeStructureObject, { "2.5": buckAgeStructureObject["2.5"] + 1 });
                } else if(buck.age < 4) {
                    Object.assign(buckAgeStructureObject, { "3.5": buckAgeStructureObject["3.5"] + 1 });
                } else if(buck.age < 5) {
                    Object.assign(buckAgeStructureObject, { "4.5": buckAgeStructureObject["4.5"] + 1 });
                } else {
                    Object.assign(buckAgeStructureObject, { "5.5+": buckAgeStructureObject["5.5+"] + 1 });
                }
                
                return true;
            });

            setBuckAgeStructure(buckAgeStructureObject);

            setBuckStructure({
                "trophy": trophyBucks.length,
                "management": managementBucks.length,
                "protect": protectBucks.length
            });
        }
    }, [bucksObject, currentProfile]);

    useEffect(() => {
        if(buckAgeStructure) {
            // Convert data into graph format
            setBuckAgeStructureChartOptions({
                "responsive": false,
                "plugins": {
                    "legend": {
                        "display": false,
                    },
                    "title": {
                        "display": false
                    }
                },
                "scales": {
                    "x": {
                      "title": {
                        "display": true, // Show X-axis label
                        "text": 'Age', // Label text
                        "font": {
                          size: 16, // Font size
                          family: 'Arial', // Font family
                        },
                        "color": '#666', // Font color
                      },
                    },
                    "y": {
                      "title": {
                        "display": true, // Show Y-axis label
                        "text": 'Number of Bucks', // Label text
                        "font": {
                          "size": 16, // Font size
                          "family": 'Arial', // Font family
                        },
                        "color": '#666', // Font color
                      },
                    }
                }
            });
            setBuckAgeStructureChartData({
                labels: Object.keys(buckAgeStructure),
                datasets: [
                    {
                        label: 'Age',
                        data: Object.values(buckAgeStructure),
                        backgroundColor: 'rgba(110, 168, 70, 1)',
                        borderColor: 'rgba(110, 168, 70, 1)',
                        borderWidth: 1,
                    },
                ],
            });
        }
    }, [buckAgeStructure]);

    useEffect(() => {
        if(orderedBucks && selectedSite.doeEstimate && selectedSite.fawnEstimate && imageStatsFetched) {
            // Convert data into graph format
            setPopulationNumbersChartOptions({
                "responsive": false,
                "plugins": {
                    "legend": {
                        "display": true,
                        "position": "bottom"
                    },
                    "title": {
                        "display": false
                    }
                }
            });
            setPopulationNumbersChartData({
                labels: ["Bucks", "Does", "Fawns"],
                datasets: [
                    {
                        data: [orderedBucks.length, selectedSite.doeEstimate, selectedSite.fawnEstimate],
                        backgroundColor: ['rgba(197, 90, 17, 1)', 'rgba(73, 90, 115, 1)', 'rgba(208, 206, 206, 1)'],
                        borderColor: ['rgba(197, 90, 17, 1)', 'rgba(73, 90, 115, 1)', 'rgba(208, 206, 206, 1)'],
                        borderWidth: 1,
                    },
                ],
            });
        }
    }, [selectedSite, orderedBucks, imageStatsFetched]);

    useEffect(() => {
        if(imageStatsFetched && buckDataFetched && populationNumbersChartData) {
            setDataFetched(true);
        }
    }, [imageStatsFetched, buckDataFetched, buckAgeStructureChartData, populationNumbersChartData]);

    // Create styles
    const styles = StyleSheet.create({
        //Example Style Parameters
        /*paragraph: {
            margin: 20,
            fontSize: 25,
            textAlign: "center" or "justify",
            backgroundColor: "HTML Color Code",
            textTransform: "uppercase",
            flexGrow: 1,
            flexDirection: "row",
            width: "100%",
            height: "100%",
        }*/
        footer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 10,
            fontSize: 12,
            flexDirection: 'row',
            justifyContent: "flex-end",
            alignItems: 'center',
        },
        header: {
            fontSize: 20,
            fontWeight: 'bold',
            marginBottom: 4,
            textDecoration: 'underline',
            textAlign: 'center'
        },
    });

    let year = new Date().toISOString().substring(0, 4);

    if(selectedSite && currentProfile && dataFetched && buckAgeStructureGraphURL && populationNumbersGraphURL && selectedSite.reportPublished) {
        return (
            <div className="pdf-viewer">
                <PDFViewer style={{ 'width': "100%", "height": "100vh" }}>
                    <Document>
                        <Page size="LETTER">
                            <View>
                                <View style={{ 'flexDirection': 'row' }}>
                                    <Image
                                        style={{ "marginTop": 8, "marginLeft": 70, "marginBottom": 10, "width": 125,}}
                                        src={{ uri: logo, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }}
                                    />
                                    <Text style={{ "fontSize": 18, "width": 300, "marginTop": 18, "marginLeft": 15, "textAlign": "right", "textTransform": "capitalize" }}>
                                        Camera Survey Report for {selectedSite.getSiteName()}
                                    </Text>
                                </View>
                            </View>
                            <View style={{ "display": "flex", "flexDirection": "row", "flexWrap": "wrap", "justifyContent": "center" }}>
                                <View style={{ "marginBottom": 10, "padding": 10, "width": "50%" }}>
                                    {orderedBucks ? (
                                        <View>
                                            <Text style={styles.header}>{selectedSite.acres}-acres {selectedSite.location}</Text>
                                            <Text style={{ "marginTop": 10 }}>Trail Cameras: {selectedSite.numberOfTrailCameras}</Text>
                                            <Text>Survey length: 9 days</Text>
                                            <Text>Total Deer Photos: {selectedSite.amountOfImages}</Text>
                                            <Text>Buck Detections: {selectedSite.buckDetections}</Text>
                                            <Text>Doe Detections: {selectedSite.doeDetections}</Text>
                                            <Text>Fawn Detections: {selectedSite.fawnDetections}</Text>
                                            {orderedBucks.length > 1 ? (
                                                <Text>Unique bucks: {orderedBucks.length} bucks</Text>
                                            ): (
                                                <Text>Unique bucks: {orderedBucks.length} buck</Text>
                                            )}
                                        </View>
                                    ) : null}
                                    <View style={{ "marginTop": 32}}>
                                        <Text style={styles.header}>Vital Stats</Text>
                                        <Text style={{ "marginTop": 10 }}>Deer density (acres/deer): {selectedSite.deerDensity}</Text>
                                        <Text>Doe:Buck ratio: {selectedSite.doeBuckRatio}</Text>
                                        <Text>Fawn:Doe ratio: {selectedSite.fawnDoeRatio}</Text>
                                    </View>
                                </View>
                                <View style={{ "width": "45%", "padding": 10, "textAlign": "center" }}>
                                    <Image
                                        style={{ "width": "90%", "height": "auto", "marginRight": 5 }}
                                        src={{ uri: selectedSite.map, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }}
                                    />
                                </View>
                                {buckStructure ? (
                                    <View style={{ "marginBottom": 10, "width": "48%", "padding": 10 }}>
                                        <Text style={styles.header}>Buck Age Structure</Text>
                                        <Image
                                            style={{ "width": 250, "height": "auto" }}
                                            src={{ uri: buckAgeStructureGraphURL }}
                                        />
                                        <Text style={{ "marginTop": 10 }}>{buckStructure.trophy} Mature bucks — shoot</Text>
                                        <Text>{buckStructure.management} Management bucks — shoot</Text>
                                        <Text>{buckStructure.protect} Immature bucks — pass</Text>
                                    </View>
                                ) : null}
                                {orderedBucks ? (
                                    <View style={{ "width": "48%", "padding": 10 }}>
                                        <Text style={styles.header}>Population Numbers</Text>
                                        <Image
                                            style={{ "width": 250, "height": "auto" }}
                                            src={{ uri: populationNumbersGraphURL }}
                                        />
                                        <Text style={{ "marginTop": 10 }}>Buck estimate: {orderedBucks.length}</Text>
                                        <Text>Doe estimate: {selectedSite.doeEstimate}</Text>
                                        <Text>Fawn estimate: {selectedSite.fawnEstimate}</Text>
                                        <Text>Total estimate: {orderedBucks.length + selectedSite.doeEstimate + selectedSite.fawnEstimate}</Text>
                                    </View>
                                ) : null}
                            </View>
                            <View style={styles.footer}>
                                <Image
                                    style={{ "width": 20, "height": 20, "marginRight": 5 }}
                                    src={{ uri: logoNoText, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }}
                                />
                                <Text style={{ "color": "#6d6e71" }}>Wildlife Investments LLC. - {year}</Text>
                            </View>
                        </Page>
                        {orderedBucks && orderedBucks.map((buck) => {
                            return(
                                <Page key={buck} size="LETTER">
                                    <View>
                                        <View style={{ "backgroundColor": "#061F22", "marginTop": 8, "padding": 10, "paddingLeft": 20, "paddingRight": 20, "width": "70%", "borderTopLeftRadius": 0, "borderTopRightRadius": 20, "borderBottomLeftRadius": 0, "borderBottomRightRadius": 20, "display": "flex", "flexDirection": "row", "alignItems": "center", "justifyContent": "center" }}>
                                            <Image
                                                style={{ "width": 35, "height": "auto", "marginRight": 32 }}
                                                src={{ uri: whiteLogoNoText, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }}
                                            />
                                            <View>
                                                <Text style={{ "fontSize": 32, "textAlign": "center", "color": "white" }}>{buck.classification} Bucks</Text>
                                                <View style={{ "display": "flex", "flexDirection": "row", "alignItems": "center", "justifyContent": "center" }}>
                                                    <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "white" }}>{buck.name}</Text>
                                                    <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "white", "marginRight": 8, "marginLeft": 8 }}>|</Text>
                                                    <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "white" }}>{buck.age}yr+</Text>
                                                    <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "white", "marginRight": 8, "marginLeft": 8 }}>|</Text>
                                                    <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "white" }}>{buck.points}pt</Text>
                                                    <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "white", "marginRight": 8, "marginLeft": 8 }}>|</Text>
                                                    {buck.classification === "Trophy" ? (
                                                        <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "green" }}>shoot</Text>
                                                    ) : (
                                                        <Text style={{ "fontSize": 18, "fontWeight": "bold", "color": "red" }}>pass</Text>
                                                    )}
                                                </View>
                                            </View>
                                        </View>
                                        <View style={{ "marginTop": 8, "display": "flex", "flexDirection": "row", "flexWrap": "wrap", "justifyContent": "center", "gap": 10 }}>
                                            {buck.images.map((image) => {
                                                return(
                                                    <View style={{ "width": "40%", "height": "auto" }} key={image}>
                                                        <Image
                                                            style={{ "height": "auto", "width": "100%" }}
                                                            src={{ uri: image, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }}
                                                        />
                                                    </View>
                                                );
                                            })}
                                        </View>
                                        <View style={{ "marginLeft": 55, "marginTop": 8 }}>
                                            <Text style={{ "fontSize": 18, "fontWeight": "bold" }}>Notes:</Text>
                                            <Text style={{ "fontSize": 14, "fontWeight": "bold", "marginTop": 8 }}>{buck.notes}</Text>
                                        </View>
                                    </View>
                                    <View style={styles.footer}>
                                        <Image
                                            style={{ "width": 20, "height": 20, "marginRight": 5 }}
                                            src={{ uri: logoNoText, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }}
                                        />
                                        <Text style={{ "color": "#6d6e71" }}>Wildlife Investments LLC. - {year}</Text>
                                    </View>
                                </Page>
                            );
                        })}
                    </Document>
                </PDFViewer>
            </div>
        );
    } else if (selectedSite && currentProfile && dataFetched) {
        return(
            <>
                <Navbar currentProfile={currentProfile} isMobile={isMobile} />
                <Loading isMobile={isMobile} text='Buck Report' />
                <Footer />
                <ChartCapture isMobile={isMobile} chartData={buckAgeStructureChartData} chartOptions={buckAgeStructureChartOptions} chartType={buckAgeStructureGraphType} setChartUrl={setBuckAgeStructureGraphURL}/>
                <ChartCapture isMobile={isMobile} chartData={populationNumbersChartData} chartOptions={populationNumbersChartOptions} chartType={populationNumbersGraphType} setChartUrl={setPopulationNumbersGraphURL}/>
            </>
        );
    } else if (selectedSite && currentProfile && !selectedSite.reportPublished) {
        return(
            <>
                <Navbar currentProfile={currentProfile} isMobile={isMobile} />
                <Error isMobile={isMobile} text={"this report has not been published yet"} />
                <Footer />
            </>
        );
    } else if (bucksError) {
        <>
            <Navbar currentProfile={currentProfile} isMobile={isMobile} />
            <Error isMobile={isMobile} text={bucksError} />
            <Footer />
        </>
    } else if(!currentProfile && typeof currentProfile === "boolean") {
        return(
            <>
                <Navbar currentProfile={currentProfile} isMobile={isMobile} />
                <Error isMobile={isMobile} type={"login"} />
                <Footer />
            </>
        );
    } else {
        return(
            <>
                <Navbar currentProfile={currentProfile} isMobile={isMobile} />
                <Loading isMobile={isMobile} text='Buck Report' />
                <Footer />
            </>
        );
    }
}
export default BuckReportPDF;